import { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useProfile } from '../context/ProfileContext';
import { useT } from '../i18n/useT';
import { F, DUR } from '../styles/theme';
import ExchangeLogo from '../components/ExchangeLogo';

// Erstkontakt: „Hol dein Portfolio rein". Geführte erste Quelle DIREKT nach dem
// Onboarding, statt den Neuling auf ein leeres Dashboard fallen zu lassen. Baut
// auf dem Trust-Versprechen auf (Abgleich mit Börse/Blockchain) und nutzt EXAKT
// die `MethodStep`-Sprache: flache Haarlinien-Zeilen, blanke Linien-Icons (sw 1.7,
// T.sub→T.text bei Hover), Marken-Chevron — KEINE Icon-Kacheln, keine Karten-Boxen.
// Eine Methode tippen → App.js öffnet den echten Flow (api/utxowallet/csv/manual).
const EASE = 'cubic-bezier(.16,1,.3,1)';

// Dünne neutrale Linien-Icons — dieselben Pfade wie der Wizard-MethodStep.
const ICON = {
  link:   <><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07L11.83 5"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></>,
  wallet: <><rect x="2" y="6" width="20" height="14" rx="2"/><path d="M16 14a2 2 0 1 1 0-4h6v4z"/><path d="M2 8V6a2 2 0 0 1 2-2h14"/></>,
  upload: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M17 8l-5-5-5 5"/><path d="M12 3v12"/></>,
  edit:   <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>,
  chevR:  <path d="M10 4.8 L15.6 12 L10 19.2"/>, // schlanker Marken-Pfeil (vgl. BrandChevron)
};

export default function ConnectFirstSource({ user, onSelect, onSkip }) {
  const { T } = useTheme();
  const { t, lang } = useT();
  const { name } = useProfile();
  const [hoverId, setHoverId] = useState(null);
  const [hoverEx, setHoverEx] = useState(null);

  const firstName = (name || user?.displayName?.split(' ')[0] || '').trim();
  const accent = T.green || T.gain || '#56B98A';

  // Reihenfolge bewusst: die abgeglichenen Quellen (Trust-Moment) zuerst.
  const methods = [
    { id: 'api',        icon: ICON.link,   title: t('cfs_exchange_title'), sub: t('cfs_exchange_sub'), trust: t('cfs_trust_exchange') },
    { id: 'utxowallet', icon: ICON.wallet, title: t('cfs_wallet_title'),   sub: t('cfs_wallet_sub'),   trust: t('cfs_trust_chain') },
    { id: 'csv',        icon: ICON.upload, title: t('cfs_csv_title'),      sub: t('cfs_csv_sub') },
    { id: 'manual',     icon: ICON.edit,   title: t('cfs_manual_title'),   sub: t('cfs_manual_sub') },
  ];
  const exchanges = ['Binance', 'Kraken', 'Coinbase', 'Bitpanda', 'Bitget'];

  // Glas-Squircle-Werte EXAKT wie die Navbar-Insel / Datencheck-Schaltfläche.
  const glassBg   = T.isDark ? 'rgba(28,30,35,0.72)' : 'rgba(240,234,222,0.92)';
  const glassEdge = T.isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.70)';

  return (
    <div style={{
      minHeight: '100vh', background: T.bg,
      display: 'flex', justifyContent: 'center', fontFamily: F.family,
    }}>
      <div style={{ width: '100%', maxWidth: 460, padding: '34px 24px 28px' }}>

        {/* Wortmarke + Fortschritt (führt aus dem Onboarding fort) */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 26 }}>
          <span style={{ fontFamily: F.brand, fontWeight: 700, fontSize: 17, color: T.text, letterSpacing: '-0.04em' }}>
            red<span style={{ color: T.red }}>.</span>
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: T.line }} />
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: T.line }} />
            <span style={{ width: 18, height: 7, borderRadius: 99, background: T.red }} />
          </div>
        </div>

        {/* Onboarding-Schablone: Koralle-Kicker · Display-Headline · Sub */}
        <div style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: T.red, fontWeight: 700, fontFamily: F.family, marginBottom: 8 }}>
          {lang === 'en' ? 'Get started' : "Los geht's"}
        </div>
        <h2 style={{
          margin: 0, fontFamily: F.display, fontSize: 27, fontWeight: 800,
          letterSpacing: '-0.03em', lineHeight: 1.12, color: T.text,
        }}>
          {firstName ? t('cfs_title', { name: firstName }) : t('cfs_title_noname')}
        </h2>
        <div style={{ marginTop: 10, fontSize: 14, color: T.sub, lineHeight: 1.5 }}>
          {t('cfs_sub')}
        </div>

        {/* Methoden — flache Haarlinien-Zeilen, blanke Linien-Icons */}
        <div style={{ marginTop: 22 }}>
          {methods.map((m, i) => (
            <button
              key={m.id}
              onClick={() => onSelect(m.id)}
              onMouseEnter={() => setHoverId(m.id)}
              onMouseLeave={() => setHoverId(h => h === m.id ? null : h)}
              style={{
                display: 'flex', alignItems: 'center', gap: 14, width: '100%',
                padding: '16px 4px', textAlign: 'left', cursor: 'pointer',
                background: hoverId === m.id ? (T.itemHover || 'transparent') : 'transparent',
                border: 'none', borderTop: `1px solid ${T.line}`,
                borderBottom: i === methods.length - 1 ? `1px solid ${T.line}` : 'none',
                borderRadius: 0, color: T.text,
                transition: `background ${DUR.fast}ms ${EASE}`,
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                stroke={hoverId === m.id ? T.text : T.sub} strokeWidth="1.7"
                strokeLinecap="round" strokeLinejoin="round"
                style={{ flexShrink: 0, transition: `stroke ${DUR.fast}ms ${EASE}` }}>
                {m.icon}
              </svg>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: F.family, fontSize: 15.5, fontWeight: 700, letterSpacing: '-0.015em', color: T.text }}>
                  {m.title}
                </div>
                <div style={{ fontSize: 12, color: T.sub, lineHeight: 1.4, marginTop: 2 }}>{m.sub}</div>
                {m.trust && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 5, fontSize: 11, fontWeight: 700, color: accent }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: accent, flexShrink: 0 }} />
                    {m.trust}
                  </div>
                )}
              </div>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                stroke={T.sub} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ flexShrink: 0 }}>
                {ICON.chevR}
              </svg>
            </button>
          ))}
        </div>

        {/* Direkt verbinden — echte Börsen-Logos, keine grauen Kacheln */}
        <div style={{ fontSize: 11.5, color: T.sub, padding: '20px 4px 12px' }}>{t('cfs_direct')}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '0 4px' }}>
          {exchanges.map((label) => (
            <button
              key={label}
              onClick={() => onSelect('api')}
              onMouseEnter={() => setHoverEx(label)}
              onMouseLeave={() => setHoverEx(h => h === label ? null : h)}
              style={{
                flex: 1, textAlign: 'center', background: 'none', border: 'none',
                padding: '4px 0', cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
              }}
            >
              <ExchangeLogo name={label} size={40} />
              <div style={{ fontSize: 10.5, color: hoverEx === label ? T.text : T.sub, transition: `color ${DUR.fast}ms ${EASE}` }}>{label}</div>
            </button>
          ))}
        </div>

        {/* Privacy + Skip (runder Glas-Knopf mit gezeichnetem Pfeil) */}
        <div style={{
          marginTop: 24, paddingTop: 18, borderTop: `1px solid ${T.line}`,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.sub}
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <path d="M12 3l7 4v5c0 5-3.5 7.5-7 9-3.5-1.5-7-4-7-9V7z" />
          </svg>
          <span style={{ fontSize: 11.5, color: T.sub, lineHeight: 1.4 }}>{t('cfs_privacy')}</span>
          <button
            onClick={onSkip}
            aria-label={t('cfs_skip')}
            title={t('cfs_skip')}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateX(2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; }}
            style={{
              marginLeft: 'auto', flexShrink: 0,
              width: 42, height: 42, borderRadius: '50%',
              background: glassBg,
              boxShadow: `inset 0 1px 0 ${glassEdge}, inset 0 0 0 1px ${T.line}`,
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
              border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: `transform ${DUR.press}ms ${EASE}`,
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.text}
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12h13.5" /><path d="M12.5 6.5L18 12l-5.5 5.5" />
            </svg>
          </button>
        </div>

      </div>
    </div>
  );
}
